import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { StatusModal, StatusModalType } from '../components/StatusModal/StatusModal';

interface StatusModalContextType {
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
  showWarning: (title: string, message: string) => void;
  closeModal: () => void;
}

const StatusModalContext = createContext<StatusModalContextType | undefined>(undefined);

export const StatusModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<StatusModalType>('success');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');

  const showModal = useCallback((type: StatusModalType, title: string, message: string) => {
    setType(type);
    setTitle(title);
    setMessage(message);
    setIsOpen(true);
  }, []);

  const showSuccess = useCallback((title: string, message: string) => {
    showModal('success', title, message);
  }, [showModal]);

  const showError = useCallback((title: string, message: string) => {
    showModal('error', title, message);
  }, [showModal]);

  const showWarning = useCallback((title: string, message: string) => {
    showModal('warning', title, message);
  }, [showModal]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <StatusModalContext.Provider value={{ showSuccess, showError, showWarning, closeModal }}>
      {children}
      <StatusModal
        isOpen={isOpen}
        onClose={closeModal}
        type={type}
        title={title}
        message={message}
      />
    </StatusModalContext.Provider>
  );
};

export const useStatusModal = () => {
  const context = useContext(StatusModalContext);
  if (!context) {
    throw new Error('useStatusModal must be used within a StatusModalProvider');
  }
  return context;
};
